import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { later, schedule } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { TourId } from 'webapp/enums/tour';

import type Board from 'webapp/models-custom/board';
import type DatawallGrid from 'webapp/models-custom/datawall-grid';
import type BlockRecord from 'webapp/models/block';

import type { EmptyBlockSetup, NewBlockData } from 'webapp/services/model-manager/blocks';

export interface Args {
	isPanelOpen: boolean;
	board: Board;
	activeDatawallGrid?: DatawallGrid | null;
	setNewBlockPositionAndSize: (blockData: NewBlockData, callback?: () => void) => void;
}

export default abstract class DesignerLeftPanelSectionInnerBaseComponent<
	T extends Args,
> extends Component<T> {
	@service('account') declare accountService: Service.Account;
	@service('popup') declare popupService: Service.Popup;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('request') declare requestService: Service.Request;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('flow/tour/designer') declare flowTourDesignerService: Service.Flow.Tour.Designer;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;

	abstract availableTourId: TourId;
	abstract checkTourStartConditions(): boolean;
	private draggable: DraggableInstance | null = null;

	listNode: HTMLElement | null = null;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		schedule('afterRender', () => {
			this.flowTourDesignerService.bindTourCheck(this.availableTourId, this, () => {
				if (this.checkTourStartConditions()) {
					this.flowTourDesignerService.startTour(this.availableTourId);
				} else {
					const isFinal = this.availableTourId === TourId.DESIGNER__SELECT_VISUALIZATION;
					this.eventManagerService.trigger(AppEvent.OPEN_LEFT_SIDEBAR, {
						tourId: this.availableTourId,
						isFinal,
					});
				}
			});
		});
	}

	// ----- Adding blocks w/ click -----

	@action
	addNewBlock(blockData: NewBlockData, blockSetupId?: EmptyBlockSetup) {
		if (this.mmBoardsService.handleFullBoard(this.args.board)) {
			return;
		}

		// added w/ click

		const boardId = this.args.board.id;
		const dateRange = this.args.board.commonBlockDateRange?.formula;
		const data: NewBlockData = Object.assign(
			{
				isGenerating: true,
				boardId,
			},
			{ ...blockData, fromMetricDateRange: dateRange },
		);

		this.args.setNewBlockPositionAndSize(data, () =>
			this.flowTourDesignerService.endTour(this.availableTourId),
		);

		return this.mmBlocksService
			.addBlockToBoardInDesigner(
				this.args.board,
				data,
				blockSetupId,
				this.args.activeDatawallGrid || undefined,
			)
			.then((blockRecord: BlockRecord) => {
				schedule('afterRender', () => {
					// blink the new block in the grid
					const boardId = blockRecord.boardId,
						blockId = blockRecord.id,
						elWithBorder = document.getElementById(`b${blockId}`)?.parentElement;

					if (elWithBorder) {
						const className = 'effect--blink';
						elWithBorder.classList.add(className);
						later(() => {
							elWithBorder.classList.remove(className);
						}, 3000);
					}

					this.navigationService.replaceRoute(`/layout-designer/${boardId}/${blockId}`);
				});

				return blockRecord;
			})
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}

	// ----- Draggable -----

	@action
	initDraggableOn(containerNode: HTMLElement) {
		const draggableElements = $(containerNode).find('li.datablock:not(.disabled)');

		draggableElements.draggable({
			appendTo: 'body',
			connectToSortable: '.view-board-content .blocks',
			containment: '#main',
			helper: 'clone',
			revert: 'invalid',
			start: (e: DragEvent) => {
				this.flowTourDesignerService.endTour(this.availableTourId);
				this.flowTourDesignerService.isTemporaryBlockerActive = true;

				if (this.mmBoardsService.handleFullBoard(this.args.board)) {
					return;
				}

				$(e.target as HTMLElement).addClass('drag-parent');

				const datawall = this.args.activeDatawallGrid;
				if (datawall) {
					datawall.onExternalDragStart();
				}
			},
			stop: (e: DragEvent) => {
				$(e.target as HTMLElement).removeClass('drag-parent');

				const datawall = this.args.activeDatawallGrid;
				if (datawall) {
					datawall.onExternalDragEnd();
				}

				this.flowTourDesignerService.isTemporaryBlockerActive = false;
			},
		});

		this.draggable = draggableElements.draggable('instance');
	}

	@action
	refreshDraggable() {
		if (!this.listNode) {
			return;
		}

		// destroy previous
		this.cleanupDraggable();

		this.initDraggableOn(this.listNode);
	}

	@action
	checkToEndTour() {
		if (
			!this.args.isPanelOpen &&
			this.flowTourDesignerService.activeTour?.id === this.availableTourId
		) {
			this.flowTourDesignerService.endTour(this.availableTourId);
		}
	}

	cleanupDraggable() {
		if (this.draggable) {
			try {
				this.draggable.destroy();
			} catch (_e) {
				// skip
			}
		}
	}

	override willDestroy() {
		super.willDestroy();
		this.flowTourDesignerService.unbindTourCheck(this.availableTourId, this);
	}
}
